/**
 * Customer dashboard (Quixo OS design, owner 8 Oct 2026): greeting + Growth Health ring; four KPI tiles; lead activity
 * chart; today's schedule; top opportunities from the latest audit; recent reports; leads overview; connected
 * integrations; recent activity; quick actions; the one promo card. Real data only: a tile whose source is not
 * connected says so and links to Integrations; nothing is zero-filled or invented.
 * Approve / reject stays on the approval detail route.
 */
import { useFullOs } from '../../../lib/flags';
import { AuditHome } from './AuditHome';
import { useState } from 'react';
import { Link } from 'react-router';
import type { Notification } from '@qm/contracts/notifications';
import { ActionTile, AreaChart, Avatar, Bars, Card, CardHead, Delta, EmptyState, ErrorState, Heading, IconTile, KpiTile, Legend, PromoCard, Rank, ScoreRing, Skeleton, StatusChip, Text, type IconTone, type StatusTone } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { useFormat } from '../../../lib/format';
import { useResource, type ResourceState } from '../../../lib/resource';
import { BffError } from '../../../lib/bff';
import { Icon, type GlyphName } from '../../../shell/Icon';
import { siteTrends, type HistoryRow } from '../audit/GrowthAuditDashboard';
import { SetupChecklist } from '../setup/SetupPage';
import { InstallApp } from './InstallApp';
import {
  greetingOf, isQualified, lastDays, leadStages, LEAD_STAGES, localDate, opportunities, pctChange, providerSummary, scheduleStatus, todayQueue, trendOf, weekStart, weeklyCounts, windowStats,
  type ConnectionLite, type LeadRow, type PendingItem, type WeekData,
} from './home-data';
import './home.css';

interface Member { id: string; email?: string; displayName?: string | null; name?: string | null; role?: string; teamRole?: string; status: string }
interface TeamData { members: Member[] }
const LEAD_PAGE = 500;
const WINDOW_DAYS = 30;
const SPARK_DAYS = 14;
const KNOWN_PROVIDERS = ['meta', 'google_ads', 'linkedin', 'tiktok', 'snap', 'gbp', 'google_search_console', 'bing_webmaster'];

export interface HomeData {
  approvals: ResourceState<PendingItem[]>;
  history: ResourceState<HistoryRow[]>;
  leads: ResourceState<LeadRow[]>;
  week: ResourceState<WeekData>;
  team: ResourceState<TeamData>;
  unread: ResourceState<{ count: number }>;
  notifications: ResourceState<Notification[]>;
  connections: ResourceState<ConnectionLite[]>;
  now: Date;
}

const rowsOf = <T,>(d: T[] | { items?: T[] } | null): T[] => (Array.isArray(d) ? d : d?.items ?? []);

/** The full Quixo OS dashboard; the Phase-1 audit home only for an audit-only workspace (lib/flags AUDIT_ONLY_FLAG). */
export function HomePage() {
  return useFullOs() ? <OsHomePage /> : <AuditHome />;
}

function OsHomePage() {
  const { me, localePrefs } = useApp();
  const on = !!me?.tenantId;
  const now = new Date();
  const start = weekStart(now, localePrefs.timezone);
  const data: HomeData = {
    approvals: useResource<PendingItem[]>(on ? '/v1/approvals/items?status=pending' : null),
    history: useResource<HistoryRow[]>(on ? '/v1/growth-audit/history?limit=100' : null),
    leads: useResource<LeadRow[]>(on ? `/v1/crm/leads?limit=${LEAD_PAGE}` : null),
    week: useResource<WeekData>(on ? `/v1/calendar/week?start=${start}&tz=${encodeURIComponent(localePrefs.timezone)}` : null),
    team: useResource<TeamData>(on ? '/v1/team/members' : null),
    unread: useResource<{ count: number }>(on ? '/v1/notifications/unread-count' : null),
    notifications: useResource<Notification[]>(on ? '/v1/notifications' : null),
    connections: useResource<ConnectionLite[]>(on ? '/v1/connectors/connections' : null),
    now,
  };
  return <HomeView {...data} />;
}

export function HomeView(d: HomeData) {
  const { t } = useApp();
  return (
    <section className="qm-page qm-dash" aria-labelledby="page-title">
      <div className="qm-dash__top">
        <Greeting team={d.team} now={d.now} />
        <GrowthHealth res={d.history} />
      </div>
      <SetupChecklist />
      <Kpis leads={d.leads} now={d.now} />
      <div className="qm-dash__grid">
        <div className="qm-dash__col qm-dash__col--main">
          <LeadActivity res={d.leads} now={d.now} />
          <Opportunities res={d.history} />
          <LeadsOverview res={d.leads} now={d.now} />
        </div>
        <div className="qm-dash__col qm-dash__col--mid">
          <Schedule week={d.week} approvals={d.approvals} now={d.now} />
          <RecentReports res={d.history} />
          <Integrations res={d.connections} />
        </div>
        <div className="qm-dash__col qm-dash__col--side">
          <RecentActivity res={d.notifications} unread={d.unread} />
          <QuickActions />
          <PromoCard id="dash-promo" title={t('shell.dash.promo.title')} body={t('shell.dash.promo.body')}
            action={<Link className="qm-button" data-variant="accent" data-size="sm" to="/audit">{t('shell.dash.promo.cta')}</Link>} />
          <InstallApp />
        </div>
      </div>
    </section>
  );
}

/** A module the plan does not include answers 402 (or 403 for a role gate): show that neutrally, not as a failure. */
function isLocked(res: ResourceState<unknown>): boolean {
  return res.status === 'error' && res.error instanceof BffError && (res.error.status === 402 || res.error.status === 403);
}

function Locked() {
  const { t } = useApp();
  return <p className="qm-dash__muted" data-testid="dash-locked">{t('shell.dash.locked')}</p>;
}

function State({ res, empty, lines = 3, children }: { res: ResourceState<unknown>; empty?: string | null; lines?: number; children: React.ReactNode }) {
  const { t } = useApp();
  if (res.status === 'loading') return <Skeleton lines={lines} label={t('common.loading')} />;
  if (isLocked(res)) return <Locked />;
  if (res.status === 'error') return <ErrorState message={t('shell.error.load')} />;
  if (empty) return <EmptyState title={empty} />;
  return <>{children}</>;
}

function Greeting({ team, now }: { team: ResourceState<TeamData>; now: Date }) {
  const { t, me, locale, localePrefs } = useApp();
  const member = team.status === 'ready' ? (team.data.members ?? []).find((m) => m.email && m.email === me?.email) : undefined;
  const name = member?.displayName || member?.name || '';
  const hello = t(`common.home.greeting.${greetingOf(now, localePrefs.timezone)}`);
  const date = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: localePrefs.timezone, numberingSystem: localePrefs.numberingSystem }).format(now);
  return (
    <header className="qm-dash__hello">
      <p className="qm-dash__greeting" data-testid="home-greeting">
        <span>{name ? t('common.home.greetingName', { greeting: hello, name }) : hello} <span aria-hidden="true">👋</span></span>
        <time dateTime={localDate(now, localePrefs.timezone)}>{date}</time>
      </p>
      <Heading level={1} id="page-title">{t('shell.dash.title')}</Heading>
      <Text tone="muted">{t('shell.dash.intro')}</Text>
    </header>
  );
}

function GrowthHealth({ res }: { res: ResourceState<HistoryRow[]> }) {
  const { t } = useApp();
  const f = useFormat();
  const site = res.status === 'ready' ? siteTrends(rowsOf(res.data)).find((s) => s.latest.overall !== null) : undefined;
  const score = site?.latest.overall ?? null;
  const trend = site ? trendOf(site.delta) : null;
  return (
    <Card className="qm-dash__health" aria-labelledby="dash-health">
      <div className="qm-dash__health-text">
        <p className="qm-dash__label" id="dash-health">{t('shell.dash.health')}</p>
        {res.status === 'loading' ? <Skeleton lines={2} label={t('common.loading')} /> : res.status === 'error' ? <ErrorState message={t('shell.error.load')} /> : score === null ? (
          <p className="qm-dash__muted">{t('shell.home.score.empty')} <Link className="qm-card__link" to="/audit">{t('shell.dash.action.audit')}</Link></p>
        ) : (
          <>
            {trend && site ? <Delta dir={trend} good={trend === 'same' ? undefined : trend === 'up'}>{f.number(Math.abs(site.delta ?? 0))}</Delta> : <span className="qm-dash__muted">{t('shell.home.score.first')}</span>}
            <small className="qm-dash__muted">{t('shell.dash.vsLastAudit')}</small>
          </>
        )}
      </div>
      {score !== null ? <ScoreRing value={score} size="lg" label={t('shell.home.score.label', { score: f.number(score) })} caption={t('shell.dash.outOf')} /> : null}
    </Card>
  );
}

function Kpis({ leads, now }: { leads: ResourceState<LeadRow[]>; now: Date }) {
  const { t, localePrefs } = useApp();
  const f = useFormat();
  const rows = leads.status === 'ready' ? rowsOf(leads.data) : [];
  const all = windowStats(rows, now, localePrefs.timezone, WINDOW_DAYS);
  const qual = windowStats(rows, now, localePrefs.timezone, WINDOW_DAYS, isQualified);
  const spark = (s: typeof all) => s.perDay.slice(-SPARK_DAYS);
  const tile = (id: string, label: string, icon: GlyphName, tone: IconTone, s: typeof all) => {
    const pct = pctChange(s.count, s.previous);
    const dir = pct === null ? 'up' : pct > 0 ? 'up' : pct < 0 ? 'down' : 'same';
    return (
      <KpiTile id={id} label={label} icon={<Icon name={icon} size={16} />} tone={tone}
        value={leads.status === 'ready' ? f.number(s.count) : '–'}
        delta={leads.status === 'ready' && pct !== null ? <Delta dir={dir} good={dir === 'same' ? undefined : dir === 'up'}>{f.number(Math.abs(pct))}%</Delta> : null}
        caption={t('shell.dash.vs30')} spark={leads.status === 'ready' ? spark(s) : undefined}
        empty={leads.status === 'error' ? (isLocked(leads) ? t('shell.dash.locked') : t('shell.error.load')) : undefined} />
    );
  };
  return (
    <div className="qm-dash__kpis">
      {tile('kpi-leads', t('shell.dash.kpi.leads'), 'leads', 'accent', all)}
      <KpiTile id="kpi-visitors" label={t('shell.dash.kpi.visitors')} icon={<Icon name="eye" size={16} />} tone="sky" value="–"
        empty={t('shell.dash.kpi.connectAnalytics')} href="/integrations" linkLabel={t('shell.dash.kpi.connect')} />
      <KpiTile id="kpi-keywords" label={t('shell.dash.kpi.keywords')} icon={<Icon name="seo" size={16} />} tone="sun" value="–"
        empty={t('shell.dash.kpi.connectSearch')} href="/integrations" linkLabel={t('shell.dash.kpi.connect')} />
      {tile('kpi-qualified', t('shell.dash.kpi.qualified'), 'user-plus', 'mint', qual)}
    </div>
  );
}

function LeadActivity({ res, now }: { res: ResourceState<LeadRow[]>; now: Date }) {
  const { t, locale, localePrefs } = useApp();
  const f = useFormat();
  const [days, setDays] = useState<7 | 30>(7);
  const rows = res.status === 'ready' ? rowsOf(res.data) : [];
  const all = windowStats(rows, now, localePrefs.timezone, days);
  const qual = windowStats(rows, now, localePrefs.timezone, days, isQualified);
  const fmtDay = (d: string) => new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC', numberingSystem: localePrefs.numberingSystem }).format(new Date(`${d}T12:00:00Z`));
  const labels = days === 7 ? all.days.map(fmtDay) : all.days.map((d, i) => (i % 5 === 0 || i === all.days.length - 1 ? fmtDay(d) : ''));
  const range = `${fmtDay(all.days[0]!)} – ${fmtDay(all.days[all.days.length - 1]!)}`;
  const id = 'dash-activity-range';
  return (
    <Card aria-labelledby="dash-activity">
      <CardHead id="dash-activity" title={t('shell.dash.activity.title')} sub={range}
        trailing={<label className="qm-dash__select"><span className="qm-sr-only" id={id}>{t('shell.dash.activity.range')}</span><select aria-labelledby={id} className="qm-input" value={days} onChange={(e) => setDays(Number(e.target.value) as 7 | 30)}><option value={7}>{t('shell.dash.activity.d7')}</option><option value={30}>{t('shell.dash.activity.d30')}</option></select></label>} />
      <State res={res} lines={5} empty={res.status === 'ready' && !all.count && !all.previous ? t('shell.dash.activity.empty') : null}>
        <Legend items={[{ name: t('shell.dash.activity.leads') }, { name: t('shell.dash.activity.qualified') }]} />
        <AreaChart label={t('shell.dash.activity.chartLabel', { days: f.number(days) })} labels={labels} format={(n) => f.number(n)}
          series={[{ name: t('shell.dash.activity.leads'), values: all.perDay }, { name: t('shell.dash.activity.qualified'), values: qual.perDay }]} />
      </State>
    </Card>
  );
}

function Schedule({ week, approvals, now }: { week: ResourceState<WeekData>; approvals: ResourceState<PendingItem[]>; now: Date }) {
  const { t, locale, localePrefs } = useApp();
  const f = useFormat();
  const loading = week.status === 'loading' || approvals.status === 'loading';
  const failed = week.status === 'error' && approvals.status === 'error';
  const today = localDate(now, localePrefs.timezone);
  const rows = todayQueue(week.status === 'ready' ? week.data : null, approvals.status === 'ready' ? rowsOf(approvals.data) : [], today, 6);
  const date = new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short', timeZone: localePrefs.timezone, numberingSystem: localePrefs.numberingSystem }).format(now);
  return (
    <Card aria-labelledby="dash-schedule">
      <CardHead id="dash-schedule" title={t('shell.dash.schedule.title')} sub={date} trailing={<Link className="qm-card__link" to="/calendar">{t('shell.dash.schedule.link')}</Link>} />
      {loading ? <Skeleton lines={4} label={t('common.loading')} /> : failed ? <ErrorState message={t('shell.error.load')} /> : !rows.length ? <EmptyState title={t('common.home.queue.empty')} /> : (
        <ul className="qm-rows qm-dash__schedule">
          {rows.map((r) => {
            const s = r.kind === 'approval' ? { status: 'pending' as const, tone: 'warning' as const } : scheduleStatus({ type: r.type, status: r.status, overdue: r.overdue });
            return (
              <li key={r.key}>
                <span className="qm-rows__time">{r.time ?? '–'}</span>
                <span className="qm-dash__dot" data-tone={s.tone} aria-hidden="true" />
                <span className="qm-rows__text"><Link to={r.href}><b><bdi>{f.text(r.title)}</bdi></b></Link><small>{r.kind === 'approval' ? t('common.home.queue.approval') : t(`shell.home.calType.${r.type in CAL_LABEL ? r.type : 'other'}`)}</small></span>
                <span className="qm-rows__trail"><StatusChip tone={s.tone}>{t(`shell.dash.status.${r.overdue ? 'overdue' : s.status}`)}</StatusChip></span>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
const CAL_LABEL: Record<string, true> = { deliverable: true, approval: true, post: true, campaign: true, meeting: true, email_campaign: true, milestone: true };

function Opportunities({ res }: { res: ResourceState<HistoryRow[]> }) {
  const { t } = useApp();
  const f = useFormat();
  const site = res.status === 'ready' ? siteTrends(rowsOf(res.data)).find((s) => s.latest.overall !== null) : undefined;
  const items = opportunities(site?.latest.factors);
  const tone: Record<string, StatusTone> = { high: 'negative', medium: 'warning', low: 'positive' };
  return (
    <Card aria-labelledby="dash-opps">
      <CardHead id="dash-opps" icon={<Icon name="sparkle" size={16} />} title={t('shell.dash.opps.title')} sub={t('shell.dash.opps.sub')} trailing={<Link className="qm-card__link" to="/audit/dashboard">{t('shell.dash.viewAll')}</Link>} />
      <State res={res} lines={5} empty={res.status === 'ready' && !items.length ? t('shell.dash.opps.empty') : null}>
        <ol className="qm-rows qm-dash__opps">
          {items.map((o, i) => {
            const name = o.known ? t(`shell.home.factor.${o.key}`) : o.label;
            return (
              <li key={o.key}>
                <Rank n={i + 1} tone={i < 2 ? 'accent' : i < 4 ? 'warning' : 'neutral'} />
                <span className="qm-rows__text"><Link to="/audit/dashboard"><b><bdi>{name}</bdi></b></Link><small>{t('shell.dash.opps.score', { score: f.number(o.score) })}</small></span>
                <span className="qm-rows__trail"><Icon name="chevron-end" size={16} /><StatusChip tone={tone[o.impact]}>{t(`shell.dash.impact.${o.impact}`)}</StatusChip></span>
              </li>
            );
          })}
        </ol>
      </State>
    </Card>
  );
}

function RecentReports({ res }: { res: ResourceState<HistoryRow[]> }) {
  const { t } = useApp();
  const f = useFormat();
  const rows = res.status === 'ready' ? [...rowsOf(res.data)].sort((a, b) => Date.parse(b.completed_at) - Date.parse(a.completed_at)).slice(0, 4) : [];
  return (
    <Card aria-labelledby="dash-reports">
      <CardHead id="dash-reports" icon={<Icon name="file" size={16} />} title={t('shell.dash.reports.title')} trailing={<Link className="qm-card__link" to="/reports">{t('shell.dash.viewAll')}</Link>} />
      <State res={res} lines={4} empty={res.status === 'ready' && !rows.length ? t('shell.dash.reports.empty') : null}>
        <ul className="qm-rows">
          {rows.map((r) => (
            <li key={r.audit_id}>
              <IconTile size="sm" tone="sky"><Icon name="file" size={16} /></IconTile>
              <span className="qm-rows__text"><Link to={`/audit/${encodeURIComponent(r.audit_id)}`}><b>{t('shell.dash.reports.audit', { tier: TIER_LABEL[r.tier] ?? r.tier })}</b></Link><small><bdi>{r.target_host}</bdi> · {f.date(r.completed_at)}</small></span>
              <span className="qm-rows__trail"><StatusChip tone="negative">PDF</StatusChip></span>
            </li>
          ))}
        </ul>
      </State>
    </Card>
  );
}
const TIER_LABEL: Record<string, string> = { essential: 'Essential', pro: 'Pro', enterprise: 'Enterprise', analyst: 'Analyst' };

function LeadsOverview({ res, now }: { res: ResourceState<LeadRow[]>; now: Date }) {
  const { t, localePrefs } = useApp();
  const f = useFormat();
  const rows = res.status === 'ready' ? rowsOf(res.data) : [];
  const stages = leadStages(rows, now, localePrefs.timezone, WINDOW_DAYS);
  const weeks = weeklyCounts(rows, now, localePrefs.timezone, 8);
  const weekLabels = lastDays(now, localePrefs.timezone, 56).filter((_, i) => i % 7 === 0);
  return (
    <Card aria-labelledby="dash-leads">
      <CardHead id="dash-leads" icon={<Icon name="leads" size={16} />} title={t('shell.dash.leadsOverview.title')} trailing={<Link className="qm-card__link" to="/leads">{t('shell.dash.last30')}</Link>} />
      <State res={res} lines={3} empty={res.status === 'ready' && !stages.new ? t('shell.home.leads.empty') : null}>
        <div className="qm-dash__funnel">
          {LEAD_STAGES.map((s) => (
            <div key={s} className="qm-dash__stage"><small>{t(`shell.dash.stage.${s}`)}</small><b>{f.number(stages[s])}</b><span className="qm-dash__muted">{stages.new ? t('shell.dash.stage.share', { pct: f.number(Math.round((stages[s] / stages.new) * 100)) }) : ''}</span></div>
          ))}
          <div className="qm-dash__stage qm-dash__stage--bars"><Bars values={weeks} labels={weekLabels} label={t('shell.dash.leadsOverview.weekly')} /></div>
        </div>
      </State>
    </Card>
  );
}

function Integrations({ res }: { res: ResourceState<ConnectionLite[]> }) {
  const { t } = useApp();
  const items = res.status === 'ready' ? providerSummary(rowsOf(res.data)) : [];
  const label = (p: string) => (KNOWN_PROVIDERS.includes(p) ? t(`shell.dash.provider.${p}`) : p);
  return (
    <Card aria-labelledby="dash-integrations">
      <CardHead id="dash-integrations" icon={<Icon name="link" size={16} />} title={t('shell.dash.integrations.title')} trailing={<Link className="qm-card__link" to="/integrations">{t('shell.dash.integrations.manage')}</Link>} />
      <State res={res} lines={2} empty={res.status === 'ready' && !items.length ? t('integrations.board.empty') : null}>
        <ul className="qm-dash__providers">
          {items.map((p) => (
            <li key={p.provider}>
              <IconTile size="sm" tone={p.connected ? 'mint' : 'sun'}><Icon name="link" size={16} /></IconTile>
              <b><bdi>{label(p.provider)}</bdi></b>
              <small data-ok={p.connected ? 'true' : undefined}>{t(p.connected ? 'shell.dash.integrations.connected' : 'shell.dash.integrations.attention')}</small>
            </li>
          ))}
        </ul>
      </State>
    </Card>
  );
}

function RecentActivity({ res, unread }: { res: ResourceState<Notification[]>; unread: ResourceState<{ count: number }> }) {
  const { t } = useApp();
  const f = useFormat();
  const rows = res.status === 'ready' ? rowsOf(res.data).slice(0, 5) : [];
  const text = (n: Notification) => t(n.messageKey, Object.fromEntries(Object.entries(n.params ?? {}).map(([k, v]) => [k, f.text(v)])));
  const count = unread.status === 'ready' ? unread.data.count : null;
  return (
    <Card aria-labelledby="dash-activity-feed">
      <CardHead id="dash-activity-feed" title={t('shell.dash.recent.title')} sub={count !== null ? t('shell.screens.home.unreadCount', { count: f.number(count) }) : undefined} trailing={<Link className="qm-card__link" to="/notifications">{t('shell.dash.viewAll')}</Link>} />
      <State res={res} lines={4} empty={res.status === 'ready' && !rows.length ? t('notifications.empty') : null}>
        <ul className="qm-rows">
          {rows.map((n) => (
            <li key={n.id}>
              <Avatar name={n.category} />
              <span className="qm-rows__text"><Link to={n.link ?? '/notifications'}><b>{text(n)}</b></Link><small>{t(`notifications.category.${n.category}`)} · {f.dateTime(n.createdAt)}</small></span>
              <span className="qm-rows__chev"><Icon name="chevron-end" size={16} /></span>
            </li>
          ))}
        </ul>
      </State>
    </Card>
  );
}

function QuickActions() {
  const { t } = useApp();
  const actions: { to: string; icon: GlyphName; key: string }[] = [
    { to: '/audit', icon: 'sparkle', key: 'audit' }, { to: '/campaigns', icon: 'campaigns', key: 'campaign' },
    { to: '/leads', icon: 'user-plus', key: 'lead' }, { to: '/reports', icon: 'file', key: 'reports' },
  ];
  return (
    <Card aria-labelledby="dash-actions">
      <CardHead id="dash-actions" title={t('shell.dash.actions.title')} />
      <div className="qm-dash__actions">
        {actions.map((a) => <ActionTile key={a.key} href={a.to} icon={<Icon name={a.icon} size={18} />} title={t(`shell.dash.action.${a.key}`)} desc={t(`shell.dash.action.${a.key}Sub`)} />)}
      </div>
    </Card>
  );
}
